import { TestFlight } from '@/mobile/TestFlight';
import { useAuthUser } from '@/src/hooks/auth';
import { useClientLayoutEffect } from '@/src/hooks/useClientLayoutEffect';
import { getVersions } from '@/src/hooks/useVersionCheck';
import { Capacitor } from '@capacitor/core';
import { isInCapacitorPlatform } from '@fabric-app/compat/capabilities/platform';
import { compareVersions } from 'compare-versions';
import { useState } from 'react';

export const useIsRevenueCatAvailable = () => {
  const user = useAuthUser();
  const [isRevenueCatAvailable, setIsRevenueCatAvailable] = useState<boolean>(
    /**
     * RevenueCat is available if:
     * - its in native mobile app
     * - Purchases capacitor plugin is available
     * - user email ends with @fabric.so
     *
     * This will allow showing the purchase options instantly for the cases mentioned above.
     */
    Boolean(
      isInCapacitorPlatform() &&
        Capacitor.isPluginAvailable('Purchases') &&
        user?.email.endsWith('@fabric.so'),
    ),
  );

  useClientLayoutEffect(() => {
    /**
     * If it's not the native mobile app or Purchases plugin is not available, then RevenueCat is not available.
     */
    if (!isInCapacitorPlatform() || !Capacitor.isPluginAvailable('Purchases')) {
      setIsRevenueCatAvailable(false);
      return;
    }

    /**
     * If the user's email ends with @fabric.so, then RevenueCat is available.
     * This allows internal testing by the team and Apple/Google reviewers.
     */
    if (user?.email.endsWith('@fabric.so')) {
      setIsRevenueCatAvailable(true);
      return;
    }

    if (isInCapacitorPlatform('capacitor-ios') && !Capacitor.isPluginAvailable('TestFlight')) {
      setIsRevenueCatAvailable(false);
      return;
    }

    /**
     * iOS: Otherwise we do a check to see if the app was installed from the app store to allow RevenueCat purchases.
     */
    if (isInCapacitorPlatform('capacitor-ios'))
      TestFlight.getInstallationOrigin().then((origin) => {
        setIsRevenueCatAvailable(origin.origin === 'appStore');
      });
    /**
     * Android: we check if version is above 0.1.22
     */ else {
      getVersions().then((versions) => {
        setIsRevenueCatAvailable(
          versions.appVersion ? compareVersions(versions.appVersion, '0.1.22') >= 0 : false,
        );
      });
    }
  }, [user?.email]);

  return isRevenueCatAvailable;
};
